import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import { addressesAPI } from '../api/address';
import { captureApiError } from '../../../lib/sentry-utils';
import { readAppLanguage } from '../../../lib/useChangeLanguage';

export interface AddressSearchResult {
  address: string;
  title: string; // Main name (street/business name) - to be bold
  subtitle: string; // Location/details - secondary info
  lat: number;
  lng: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fullDetails: any;
}

/**
 * Поиск адреса для шторки на экране адреса.
 *
 * `center` — текущая точка на карте: выдача смещается к ней, чтобы «Пушкина»
 * находила улицу в своём городе, а не в другом конце страны. Сама выдача
 * ограничена Узбекистаном (см. addressesAPI.searchAddresses).
 */
export const useAddressSearch = (
  query: string,
  center?: { lat: number; lng: number }
) => {
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce the query
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!query.trim()) {
      setDebouncedQuery('');
      return;
    }

    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, 400);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  const language = readAppLanguage();
  // Округляем до сотых долей градуса (~1 км): иначе каждое движение карты
  // делало бы ключ новым и сбрасывало кеш выдачи.
  const viewboxKey = center
    ? `${center.lat.toFixed(2)},${center.lng.toFixed(2)}`
    : '';

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['search-addresses', debouncedQuery, viewboxKey, language],
    queryFn: async () => {
      if (!debouncedQuery.trim()) return [];

      try {
        const res = await addressesAPI.searchAddresses(debouncedQuery, {
          center,
          language,
        });
        const items = res.data || [];

        if (items.length === 0) return [];

        const results: AddressSearchResult[] = items.map(item => {
          const lat = parseFloat(item.lat);
          const lng = parseFloat(item.lon);
          const address = item.display_name || '';
          const addressParts = address.split(',').map(part => part.trim());
          const title = item.name || addressParts[0] || '';
          const subtitle =
            item.address?.city ||
            item.address?.town ||
            item.address?.village ||
            item.address?.suburb ||
            item.address?.state ||
            item.address?.country ||
            '';

          return {
            address,
            title: title.trim(),
            subtitle,
            lat,
            lng,
            fullDetails: item,
          };
        });

        return results;
      } catch (error) {
        console.error(
          'SearchAddresses error:',
          JSON.stringify(error, Object.getOwnPropertyNames(error), 2)
        );
        captureApiError(error, '/nominatim/search', 'GET');
        throw error;
      }
    },
    enabled: !!debouncedQuery.trim(),
  });

  return {
    results: data || [],
    isLoading,
    isError,
    error,
  };
};
